/**
 * Serviço: imageUploadService.js
 * Gerencia upload de imagens para Firebase Storage com compressão
 */
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import imageCompression from 'browser-image-compression';

/**
 * Comprime e faz upload de uma imagem para o Firebase Storage
 * @param {File} file - Arquivo de imagem
 * @param {string} userId - ID do usuário (para organizar no Storage)
 * @param {string} folder - Pasta no Storage (ex: 'profiles', 'barbershops')
 * @returns {Promise<{success: boolean, data?: {url: string, path: string}, error?: string}>}
 */
export const uploadImage = async (file, userId = 'temp', folder = 'profiles') => {
  try {
    // Validar arquivo
    if (!file) {
      return { success: false, error: 'Nenhum arquivo fornecido' };
    }

    // Validar tipo de arquivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return { 
        success: false, 
        error: 'Formato inválido. Use JPG, PNG, GIF ou WebP' 
      };
    }

    // Compressão da imagem com browser-image-compression
    console.log('🖼️ Tamanho original:', (file.size / 1024 / 1024).toFixed(2), 'MB');
    
    const options = {
      maxSizeMB: 0.5, // Máximo 500KB
      maxWidthOrHeight: 800, // Máximo 800px de largura/altura
      useWebWorker: true,
      fileType: 'image/jpeg', // Converter tudo para JPEG para melhor compressão
    };

    const compressedFile = await imageCompression(file, options);
    console.log('✅ Tamanho comprimido:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');

    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const fileName = `${userId}_${timestamp}_${randomString}.jpg`;
    const filePath = `${folder}/${fileName}`;

    // Upload para Firebase Storage
    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, compressedFile);

    // Obter URL de download
    const downloadURL = await getDownloadURL(storageRef);

    console.log('🎉 Upload concluído:', downloadURL);

    return {
      success: true,
      data: {
        url: downloadURL, // URL da imagem
        path: filePath, // Caminho no Storage (útil para deletar depois)
        size: compressedFile.size,
      }
    };

  } catch (error) {
    console.error('❌ Erro no upload da imagem:', error);
    return { 
      success: false, 
      error: error.message || 'Erro ao fazer upload. Tente novamente.' 
    };
  }
};

/**
 * Deleta uma imagem do Firebase Storage
 * @param {string} imagePath - Caminho da imagem no Storage
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteImage = async (imagePath) => {
  try {
    if (!imagePath) {
      return { success: false, error: 'Nenhum caminho fornecido' };
    }

    const storageRef = ref(storage, imagePath);
    await deleteObject(storageRef);

    return { success: true };
  } catch (error) {
    console.error('Erro ao deletar imagem:', error);
    return { 
      success: false, 
      error: error.message || 'Erro ao deletar imagem' 
    };
  }
};

/**
 * Converte uma imagem para base64 (útil para preview local)
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<string>} - URL base64 da imagem
 */
export const getImagePreview = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Valida se um arquivo é uma imagem válida
 * @param {File} file - Arquivo para validar
 * @returns {{valid: boolean, error?: string}}
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'Nenhum arquivo fornecido' };
  }

  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Formato inválido. Use JPG, PNG, GIF ou WebP' 
    };
  }

  // Tamanho máximo antes da compressão: 10MB
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: 'Imagem muito grande. Máximo 10MB' 
    };
  }

  return { valid: true };
};